import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@lib/utils/helpers";
import { useUserRole } from "@/hooks/useUserRole";
import { useAppSelector } from "@/app/hooks";
import {
  getRoleInitials,
  COMPANY_SPECIFIC_ROLES,
  PROPERTY_ROLES,
} from "@/constants/roles";
import { useRoleConfigLookup } from "@/features/auth/hooks/useRoleConfigLookup";
import { resolveRoleTitle } from "@/features/auth/utils/roleConfig";
import { RoleSelectionDialog } from "@/features/auth/components/RoleSelectionDialog";
import {
  Home,
  ShoppingCart,
  Users as UsersIcon,
  Building2,
  HomeIcon,
  UserCog,
  Package,
  FileText,
  Users,
  DollarSign,
  BarChart3,
  FileBarChart,
  ChevronRight,
  Settings,
  HelpCircle,
  Menu,
  Mail,
  MailCheck,
  Layers,
  Network,
  Monitor,
  User,
  Megaphone,
  Activity,
  Upload,
} from "lucide-react";
import { isReimbursementsAccessDeniedRole } from "@/features/reimbursements/utils/reimbursementsAccess";
import { SidebarLogo } from "@/components/layout/SidebarLogo";
import type { NavItem } from "./DashboardSidebar.types";

/** Active state based on pathname prefix. */
function isNavItemLocationActive(
  item: NavItem,
  pathname: string,
  _search: string,
): boolean {
  // The Home root (`/dashboard`) is a prefix of every dashboard route, so it
  // must match exactly — otherwise Home lights up on every sub-route too.
  if (item.path === "/dashboard") return pathname === "/dashboard";
  return pathname === item.path || pathname.startsWith(item.path + "/");
}

function childBranchIsActive(
  child: NavItem,
  pathname: string,
  search: string,
): boolean {
  if (child.children && child.children.length > 0) {
    return (
      isNavItemLocationActive(child, pathname, search) ||
      child.children.some((g) => isNavItemLocationActive(g, pathname, search))
    );
  }
  return isNavItemLocationActive(child, pathname, search);
}

/**
 * Main dashboard sidebar (Configuration, Companies, Reports, etc.).
 * Kept separate from InviteeSidebar so invitee-prod layout code stays untouched.
 */
export function DashboardSidebar() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const {
    hasPolicy,
    hasAccess,
    isLibertyRole,
    isAdmin,
    orgId,
    companyId,
    companyName,
    propertyId,
    propertyName,
    accessibleMenu,
    roleName,
  } = useUserRole();
  const user = useAppSelector((state) => state.auth.user);
  const salesRole = user?.current_role?.sales_role ?? "";
  const roleConfigLookup = useRoleConfigLookup();

  const currentRoleName = user?.current_role?.role_name ?? "";
  const currentRoleId = user?.current_role?.role_id;
  const roleDisplayName = resolveRoleTitle(currentRoleName, roleConfigLookup);
  const roleInitials = getRoleInitials(currentRoleName);

  const inCompanySpecificRole = (COMPANY_SPECIFIC_ROLES as readonly string[]).includes(currentRoleName);
  const inPropertyRole = (PROPERTY_ROLES as readonly string[]).includes(currentRoleName);

  // Reset expanded nav groups when role changes (sidebar menu is role-scoped).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- collapse stale groups after role switch
    setOpenGroups({});
  }, [currentRoleId]);

  // Check if user has multiple roles (to show clickable role selector)
  const hasMultipleRoles = user?.roles
    ? Object.keys(user.roles).length > 1
    : false;

  // Detect mobile/tablet viewport (<1024px) - hamburger visible on phones, tablets, small desktops
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close sidebar on route change if mobile
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mobile-only: route changes must collapse the drawer
    if (isMobile) setIsExpanded(false);
  }, [location.pathname, isMobile]);

  /**
   * Angular: *ngIf="(org_id === 1 && hasPolicy(['invite_menu'])) ||
   *                 (property_roles.includes(role_name) && isPropertyListed && hasPolicy(['invite_menu']))"
   */
  const isPropertyListed = user?.current_role?.property_listed === "1";
  const canSeeInviteMenu =
    hasPolicy(["invite_menu"]) && (orgId === 1 || (inPropertyRole && isPropertyListed));
  const canSeeYardiInvite = canSeeInviteMenu;

  /**
   * Angular: *ngIf="accessibleMenu?.application && org_id === 1 && hasPolicy(['application_menu'])"
   */
  const canSeeApplications =
    !!accessibleMenu?.application && orgId === 1 && hasPolicy(["application_menu"]);

  /**
   * Angular: *ngIf="hasPolicy(['resident_menu']) && org_id === 1"
   * (Allow on React for all roles that have policy, including Property Assistant)
   */
  const canSeeResidents = hasPolicy(["resident_menu"]);

  /**
   * Angular: default_menu holders, plus roles that open Access Denied on navigate
   * (EXTERNAL_INSURANCE / ROLE_LIBERTY_FIN_MGR).
   */
  const canSeeReimbursements =
    hasPolicy(["default_menu"]) || isReimbursementsAccessDeniedRole(roleName);

  /**
   * Angular: *ngIf="hasPolicy(['reports_menu'])"
   */
  const canSeeReports = hasPolicy(["reports_menu"]);

  /**
   * Configuration sub-items — ported from `layout.component.html` (showconfig + admin blocks).
   */
  const showUnderwritingNav = hasAccess("underwriting_config");
  const showPropertyMaintenanceNav = hasPolicy(["property_maintenance_menu"]);
  const showEmailTemplatesNonAdmin = !isAdmin && hasPolicy(["email_template_menu"]);
  const showEmailTemplatesAdmin = isAdmin && !!accessibleMenu?.email_templates;
  /** Mailtrap Logs hidden from All Reports — keep sidebar entry off too. */
  const showMailtrapNav = false;
  const showJobsNav = isAdmin && !!accessibleMenu?.jobs;
  const showTeamsNav =
    hasPolicy(["sales_teams_config_menu"]) || salesRole === "SALES_LEAD";
  /**
   * Angular "Business Config" group: visible when the user holds either sub-module policy.
   * (layout.component.html:678 — `screening_info_requests_config_menu` OR `frequently_used_notes_config_menu`)
   */
  const showBusinessConfigNav =
    hasPolicy(["screening_info_requests_config_menu"]) ||
    hasPolicy(["frequently_used_notes_config_menu"]);

  /**
   * Angular company shortcut:
   * *ngIf="hasPolicy(['company_menu']) && (company_roleslist.includes(role) || property_roleslist.includes(role))"
   * company_roleslist = company_specific_roles (COMPANY_SPECIFIC_ROLES)
   */
  const showCompanyShortcut = hasPolicy(["company_menu"]) && (inCompanySpecificRole || inPropertyRole);

  /**
   * Angular company list (else block): *ngIf="accessibleMenu?.company && hasPolicy(['company_menu'])"
   */
  const showCompanyList = !showCompanyShortcut && !!accessibleMenu?.company && hasPolicy(["company_menu"]);

  /**
   * Angular property shortcut: *ngIf="hasPolicy(['property_menu']) && property_roleslist.includes(role)"
   */
  const showPropertyShortcut = hasPolicy(["property_menu"]) && inPropertyRole;

  /**
   * Angular property list (else block): *ngIf="hasPolicy(['property_menu'])"
   */
  const showPropertyList = !showPropertyShortcut && hasPolicy(["property_menu"]);

  /** Angular layout: *ngIf="hasPolicy(['product_menu'])" — "Products" above User Management */
  const showProductsNav = hasPolicy(["product_menu"]);
  /** Angular layout: *ngIf="hasPolicy(['model_menu'])" — "Models" directly under Products */
  const showModelsNav = hasPolicy(["model_menu"]);

  const navigation: NavItem[] = [
    { name: "Home", path: "/dashboard", icon: Home },
    {
      name: "Invite",
      path: "/invite",
      icon: ShoppingCart,
      visible: canSeeInviteMenu,
    },
    {
      name: "Yardi-Invite",
      path: "/yardiinvite",
      icon: UsersIcon,
      visible: canSeeYardiInvite,
    },
    ...(showCompanyShortcut
      ? [{
        name: companyName || "Company",
        path: `/company/edit/${companyId}`,
        icon: Building2,
        visible: true,
      }]
      : [{
        name: "Companies",
        path: "/company",
        icon: Building2,
        visible: showCompanyList,
      }]
    ),
    ...(showPropertyShortcut
      ? [{
        name: propertyName || "Property",
        path: `/property/edit/${propertyId}`,
        icon: HomeIcon,
        visible: true,
      }]
      : [{
        name: "Properties",
        path: "/property",
        icon: HomeIcon,
        visible: showPropertyList,
      }]
    ),
    {
      name: "Products",
      path: "/product",
      icon: Package,
      visible: showProductsNav,
    },
    {
      name: "Models",
      path: "/model",
      icon: Layers,
      visible: showModelsNav,
    },
    {
      name: "User Management",
      path: "/user-management",
      icon: UserCog,
      visible: !!accessibleMenu?.user_management,
    },
    {
      name: "Applications",
      path: "/application",
      icon: FileText,
      visible: canSeeApplications,
    },
    {
      name: "Residents",
      path: "/resident",
      icon: Users,
      visible: canSeeResidents,
    },
    {
      name: "Reimbursements",
      path: "/defaults",
      icon: DollarSign,
      visible: canSeeReimbursements,
    },
    {
      name: "Reports",
      path: "/reports",
      icon: BarChart3,
      visible: canSeeReports,
    },
    {
      name: "Reports Scheduler",
      path: "/reports-scheduler",
      icon: FileBarChart,
      visible: hasPolicy(["reports_scheduler_menu"]),
    },
    {
      name: "Configuration",
      path: "/config",
      icon: Settings,
      visible: isAdmin || !!accessibleMenu?.underwriting_config || hasPolicy(["property_maintenance_menu"]) || isLibertyRole,
      children: [
        {
          name: "Underwriting Config",
          path: "/underwriting-config",
          icon: FileText,
          visible: showUnderwritingNav,
        },
        {
          name: "Property Maintenance",
          path: "/property-maintenance",
          icon: HomeIcon,
          visible: showPropertyMaintenanceNav,
          children: [
            {
              name: "Transfer",
              path: "/transfer-list",
              icon: Network,
              visible: hasPolicy(["transfer_menu"]),
            },
            {
              name: "Import",
              path: "/import-data",
              icon: Upload,
              visible: hasPolicy(["import_data_menu"]),
            },
            {
              name: "Teams",
              path: "/sales-team",
              icon: Users,
              visible: showTeamsNav,
            },
          ],
        },
        {
          name: "Email Templates",
          path: "/email-templates",
          icon: MailCheck,
          visible: showEmailTemplatesNonAdmin,
        },
        {
          name: "Impact Notes",
          path: "/impact-notes",
          icon: Monitor,
          visible: hasPolicy(["impact_notes_menu"]),
        },
        {
          name: "Stock Registry",
          path: "/stock",
          icon: Layers,
          visible: hasPolicy(["stock_view_registry"]),
        },
        {
          name: "Business Config",
          path: "/screening-info-requests",
          icon: FileText,
          visible: showBusinessConfigNav,
          children: [
            {
              name: "Screening Info Requests",
              path: "/screening-info-requests",
              icon: FileText,
              visible: hasPolicy(["screening_info_requests_config_menu"]),
            },
            {
              name: "Reimbursement Internal Notes",
              path: "/frequently-used-notes",
              icon: FileText,
              visible: hasPolicy(["frequently_used_notes_config_menu"]),
            },
          ],
        },
        { name: "Whats New", path: "/version", icon: FileText, visible: isAdmin },
        {
          name: "Organizations",
          path: "/organizations",
          icon: Network,
          visible: isAdmin,
        },
        {
          name: "Document Management",
          path: "/terms-and-conditions",
          icon: FileText,
          visible: isAdmin,
          children: [
            {
              name: "Terms & Conditions",
              path: "/terms-and-conditions",
              icon: FileText,            },
            {
              name: "Service Agreement",
              path: "/gdpr-consent/gdpr-document",
              icon: FileText,            },
          ],
        },
        {
          name: "Email Templates",
          path: "/email-templates",
          icon: MailCheck,
          visible: showEmailTemplatesAdmin,
        },
        { name: "Mailtrap", path: "/mailtrap", icon: Mail, visible: showMailtrapNav },
        { name: "Queued Jobs", path: "/jobs/queued", icon: Layers, visible: showJobsNav },
        { name: "Cron Job", path: "/jobs/cron", icon: Layers, visible: showJobsNav },
        { name: "Cron Monitor", path: "/jobs/cron-monitor", icon: Activity, visible: showJobsNav },
        { name: "Roles", path: "/user-roles", icon: User, visible: isAdmin },
        { name: "Policy", path: "/policy", icon: FileText, visible: isAdmin },
        {
          name: "System Announcement",
          path: "/system-announcement",
          icon: Megaphone,
          visible: isAdmin,
        },
        { name: "Config", path: "/config", icon: FileText, visible: isAdmin },
        {
          name: "Report Description",
          path: "/report-description",
          icon: Building2,
          visible: isAdmin,
        },
        {
          name: "Dashboard Tiles",
          path: "/dashboard-tiles",
          icon: BarChart3,
          visible: isAdmin,
        },
        {
          name: "Notification Settings",
          path: "/notification-settings",
          icon: Mail,
          visible: isAdmin,
        },
      ],
    },
    {
      name: "Help / About",
      path: "/help-about",
      icon: HelpCircle,    },
  ];

  const handleMouseEnter = () => {
    if (!isMobile) setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    if (!isMobile) setIsExpanded(false);
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-[998]"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Floating Hamburger (only when sidebar is off-screen on mobile) */}
      {isMobile && !isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="fixed top-3 left-4 z-[997] p-2 bg-white rounded-md shadow-md text-gray-700"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 h-screen bg-[#3B4D66] text-white transition-all duration-300 ease-in-out z-[999] shadow-xl flex flex-col",
          isMobile
            ? isExpanded
              ? "w-64 translate-x-0"
              : "w-64 -translate-x-full"
            : isExpanded
              ? "w-64"
              : "w-16",
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Burger menu row — height matches dashboard chrome (3.2rem) */}
        <div className="flex h-[3.2rem] min-h-[3.2rem] max-h-[3.2rem] flex-shrink-0 items-center border-b border-gray-700/50 bg-[#3B4D66]">
          {isExpanded ? (
            <button
              onClick={() => setIsExpanded(false)}
              className="flex items-center justify-center w-16 h-[3.2rem] text-white hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          ) : (
            <button
              onClick={() => setIsExpanded(true)}
              className="flex items-center justify-center w-full h-[3.2rem] text-white hover:bg-white/10 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}
        </div>

        <SidebarLogo
          isExpanded={isExpanded}
          orgLogo={user?.current_role?.org_logo}
          orgSwooshLogo={user?.current_role?.org_swoosh_logo}
          orgWebsite={user?.current_role?.org_website}
          alt={user?.current_role?.org_name || "Liberty Rent"}
        />

        {/* User Info */}
        <div className="px-4 py-4 border-b border-gray-700/50">
          {isExpanded ? (
            <div className="transition-opacity duration-300">
              <p className="text-xs text-gray-400 mb-1">Role</p>
              <p className="text-sm font-semibold text-white break-words">
                {roleDisplayName}
              </p>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-xs font-bold text-white shadow-lg">
                {roleInitials}
              </div>
            </div>
          )}
        </div>

        {/* Role Selector - clickable to switch roles (mirrors Angular showUserRoleDialog) */}
        {hasMultipleRoles && (
          <div className="px-4 py-3 border-b border-gray-700/50">
            {isExpanded ? (
              <div className="transition-opacity duration-300">
                <p className="text-xs text-gray-400 mb-2">Switch Role</p>
                <button
                  onClick={() => setIsRoleDialogOpen(true)}
                  className="w-full bg-[#2C3E50] hover:bg-[#243442] rounded-md px-3 py-2.5 text-sm flex items-center justify-between gap-2 transition-all duration-200 shadow-sm hover:shadow-md group"
                >
                  <span className="text-white font-medium min-w-0 flex-1 text-left break-words leading-snug">
                    {roleDisplayName}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsRoleDialogOpen(true)}
                className="flex justify-center w-full hover:bg-white/10 rounded-md py-1 transition-colors"
                title="Switch Role"
              >
                <UserCog className="h-5 w-5 text-gray-300" />
              </button>
            )}
          </div>
        )}

        {/* Navigation - Scrollable */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-500 min-h-0">
          {navigation
            .filter((item) => item.visible !== false)
            .map((item) => {
              const Icon = item.icon;

              // Parent item with children → expandable group
              if (item.children && item.children.length > 0) {
                const childActive = item.children.some((child) =>
                  childBranchIsActive(
                    child,
                    location.pathname,
                    location.search,
                  ),
                );
                // Angular: `(click)="showconfig = !showconfig"` — pure toggle.
                // Use `?? childActive` so a deep-linked child still expands once, but an
                // explicit `false` wins so re-clicking Config can collapse while on a sub-route.
                const groupOpen = openGroups[item.name] ?? childActive;
                const showChildren = isExpanded && groupOpen;

                return (
                  <div key={item.name}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isExpanded) setIsExpanded(true);
                        setOpenGroups((prev) => ({
                          ...prev,
                          [item.name]: !(prev[item.name] ?? childActive),
                        }));
                      }}
                      className={cn(
                        "w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 group relative",
                        childActive
                          ? "bg-[#4A5F7F]/60 text-white"
                          : "text-gray-300 hover:bg-[#4A5F7F]/80 hover:text-white hover:shadow-sm",
                      )}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 flex-shrink-0 transition-all duration-200",
                          childActive
                            ? "text-teal-400"
                            : "text-gray-400 group-hover:text-white group-hover:scale-110",
                        )}
                      />
                      {isExpanded && (
                        <>
                          <span className="ml-3 flex-1 text-left">{item.name}</span>
                          <ChevronRight
                            className={cn(
                              "h-4 w-4 text-gray-400 transition-transform duration-200",
                              groupOpen && "rotate-90",
                            )}
                          />
                        </>
                      )}
                    </button>
                    {showChildren && (
                      <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
                        {item.children
                          .filter((child) => child.visible !== false)
                          .map((child) => {
                            const childIsActive = isNavItemLocationActive(
                              child,
                              location.pathname,
                              location.search,
                            );

                            // Nested expandable — e.g. Property Maintenance / Document Management inside Configuration.
                            if (child.children && child.children.length > 0) {
                              const ChildIcon = child.icon;
                              const grandActive = child.children.some((g) =>
                                isNavItemLocationActive(
                                  g,
                                  location.pathname,
                                  location.search,
                                ),
                              );
                              const nestedKey = `${item.name}/${child.name}`;
                              const nestedActive = grandActive || childIsActive;
                              // Same Angular toggle semantics as top-level Configuration.
                              const nestedOpen = openGroups[nestedKey] ?? nestedActive;
                              return (
                                <div key={child.path}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenGroups((prev) => ({
                                        ...prev,
                                        [nestedKey]: !(prev[nestedKey] ?? nestedActive),
                                      }))
                                    }
                                    className={cn(
                                      "w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md transition-all duration-150 group",
                                      childIsActive || grandActive
                                        ? "bg-[#4A5F7F] text-white"
                                        : "text-gray-300 hover:bg-[#4A5F7F]/60 hover:text-white",
                                    )}
                                  >
                                    <ChildIcon
                                      className={cn(
                                        "h-4 w-4 flex-shrink-0",
                                        childIsActive || grandActive
                                          ? "text-teal-400"
                                          : "text-gray-400 group-hover:text-white",
                                      )}
                                    />
                                    <span className="flex-1 truncate text-left">{child.name}</span>
                                    <ChevronRight
                                      className={cn(
                                        "h-3 w-3 text-gray-400 transition-transform duration-200",
                                        nestedOpen && "rotate-90",
                                      )}
                                    />
                                  </button>
                                  {nestedOpen && (
                                    <div className="mt-1 ml-3 pl-3 border-l border-white/10 space-y-1">
                                      {child.children
                                        .filter((gc) => gc.visible !== false)
                                        .map((gc) => {
                                          const GcIcon = gc.icon;
                                          const gcActive = isNavItemLocationActive(
                                            gc,
                                            location.pathname,
                                            location.search,
                                          );
                                          return (
                                            <Link
                                              key={gc.path}
                                              to={gc.path}
                                              className={cn(
                                                "flex items-center gap-2 px-3 py-2 text-[11px] font-medium rounded-md transition-all duration-150 group",
                                                gcActive
                                                  ? "bg-[#4A5F7F] text-white"
                                                  : "text-gray-300 hover:bg-[#4A5F7F]/60 hover:text-white",
                                              )}
                                            >
                                              <GcIcon
                                                className={cn(
                                                  "h-3.5 w-3.5 flex-shrink-0",
                                                  gcActive
                                                    ? "text-teal-400"
                                                    : "text-gray-400 group-hover:text-white",
                                                )}
                                              />
                                              <span className="truncate">{gc.name}</span>
                                            </Link>
                                          );
                                        })}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            const ChildIcon = child.icon;
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={cn(
                                  "flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md transition-all duration-150 group",
                                  childIsActive
                                    ? "bg-[#4A5F7F] text-white"
                                    : "text-gray-300 hover:bg-[#4A5F7F]/60 hover:text-white",
                                )}
                              >
                                <ChildIcon
                                  className={cn(
                                    "h-4 w-4 flex-shrink-0",
                                    childIsActive
                                      ? "text-teal-400"
                                      : "text-gray-400 group-hover:text-white",
                                  )}
                                />
                                <span className="truncate">{child.name}</span>
                              </Link>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              }

              // Leaf item (no children)
              const isActive = isNavItemLocationActive(
                item,
                location.pathname,
                location.search,
              );

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 group relative",
                    isActive
                      ? "bg-[#4A5F7F] text-white shadow-md"
                      : "text-gray-300 hover:bg-[#4A5F7F]/80 hover:text-white hover:shadow-sm",
                  )}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-teal-500 rounded-r-full" />
                  )}
                  <Icon
                    className={cn(
                      "h-5 w-5 flex-shrink-0 transition-all duration-200",
                      isActive
                        ? "text-teal-400"
                        : "text-gray-400 group-hover:text-white group-hover:scale-110",
                    )}
                  />
                  {isExpanded && (
                    <span className="ml-3 transition-opacity duration-300 opacity-100">
                      {item.name}
                    </span>
                  )}
                </Link>
              );
            })}
        </nav>
      </aside>

      {/* Role Selection Dialog */}
      <RoleSelectionDialog
        isOpen={isRoleDialogOpen}
        onClose={() => setIsRoleDialogOpen(false)}
      />
    </>
  );
}
